import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
// Import only what we need from Spine for better tree-shaking
import {
    ManagedWebGLRenderingContext,
    SceneRenderer,
    AssetManager,
    AtlasAttachmentLoader,
    SkeletonJson,
    Skeleton,
    AnimationStateData,
    AnimationState,
    Vector2
} from '@esotericsoftware/spine-webgl';

// Styled components
const PlayerContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const CanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 20px;
    //height: 400px;

    canvas {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const Controls = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 15px;
    gap: 20px;
`;

const ControlGroup = styled.div`
    display: flex;
    flex-direction: column;
`;

const ControlLabel = styled.label`
    margin-bottom: 5px;
    font-size: 14px;
`;

const SelectControl = styled.select`
    padding: 5px 10px;
    min-width: 150px;
`;

const LoadingErrorOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 16px;
`;

const PosterImage = styled.img`
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-color: transparent;
    transform: translate(-50%, -50%) scale(0.82);
`;

function InteractiveSpinePlayer({
                                    assetPath,
                                    jsonFile,
                                    atlasFile,
                                    initialAnimation = null,
                                    initialSkin = 'default',
                                    showControls = true,
                                    initialTrackAnimations = null,
                                    poster = null,
                                    className = '',
                                    style = {},
                                }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const animFrameRef = useRef(null);
    const spineInstanceRef = useRef({});
    const [isInView, setIsInView] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [availableAnimations, setAvailableAnimations] = useState([]);
    const [availableSkins, setAvailableSkins] = useState([]);
    const [currentAnimation, setCurrentAnimation] = useState(initialAnimation);
    const [currentSkin, setCurrentSkin] = useState(initialSkin);

    // Lazy load - only initialize when in viewport
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        // Once in view, stop observing
                        observer.disconnect();
                    }
                });
            },
            {
                rootMargin: '100px', // Start loading 100px before entering viewport
            }
        );

        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Main initialization effect - only runs when in view
    useEffect(() => {
        // Don't initialize until component is in viewport
        if (!isInView) return;

        console.log("Initializing Spine Player...");
        if (!canvasRef.current) return;

        setIsLoading(true);
        setError(null);

        // References for cleanup
        const canvas = canvasRef.current;
        let lastFrameTime = Date.now() / 1000;

        try {
            // Initialize WebGL context
            const context = new ManagedWebGLRenderingContext(canvas, {
                alpha: true
            });

            // Create renderer
            const renderer = new SceneRenderer(canvas, context);

            // Create asset manager
            const assetManager = new AssetManager(context);

            // Define asset paths
            const atlasPath = `${assetPath}/${atlasFile}`;
            const jsonPath = `${assetPath}/${jsonFile}`;

            // Load assets
            console.log(`Loading assets: ${atlasPath}, ${jsonPath}`);
            assetManager.loadTextureAtlas(atlasPath);
            assetManager.loadText(jsonPath);

            // Check loading progress
            const checkLoading = () => {
                if (assetManager.isLoadingComplete()) {
                    console.log("Assets loaded successfully");

                    if (assetManager.hasErrors()) {
                        console.error("Asset loading errors:", assetManager.getErrors());
                        setError("Failed to load Spine assets. Check console for details.");
                        setIsLoading(false);
                        return;
                    }

                    try {
                        // Parse atlas
                        const atlas = assetManager.get(atlasPath);
                        const atlasLoader = new AtlasAttachmentLoader(atlas);
                        const skeletonJson = new SkeletonJson(atlasLoader);

                        // Scale if needed
                        skeletonJson.scale = 1.0;

                        // Parse skeleton data
                        const skeletonData = skeletonJson.readSkeletonData(assetManager.get(jsonPath));

                        // Create skeleton
                        const skeleton = new Skeleton(skeletonData);

                        // Create animation state
                        const animStateData = new AnimationStateData(skeletonData);
                        animStateData.defaultMix = 0.2; // Smooth transitions
                        const animState = new AnimationState(animStateData);

                        // Store important instances for later access
                        spineInstanceRef.current = {
                            skeleton,
                            animState,
                            renderer,
                            context
                        };

                        // Get available animations and skins
                        const anims = skeletonData.animations.map(a => a.name);
                        const skins = skeletonData.skins.map(s => s.name);
                        setAvailableAnimations(anims);
                        setAvailableSkins(skins);

                        // Apply initial skin
                        const finalSkin = skins.includes(initialSkin) ? initialSkin : 'default';
                        skeleton.setSkinByName(finalSkin);
                        setCurrentSkin(finalSkin);

                        // Setup pose
                        skeleton.setToSetupPose();

                        // FIX: Handle updateWorldTransform correctly for Spine 4.2
                        // FIX: Based on error logs, only the empty object approach works
                        console.log("Using empty object for physics parameter");
                        skeleton.updateWorldTransform({});


                        //Anim
                        if (initialTrackAnimations && Array.isArray(initialTrackAnimations)) {
                            console.log("Setting multiple track animations:", initialTrackAnimations);
                            initialTrackAnimations.forEach(animInfo => {
                                if (animInfo && typeof animInfo.track === 'number' && animInfo.name && anims.includes(animInfo.name)) {
                                    try {
                                        animState.setAnimation(animInfo.track, animInfo.name, animInfo.loop ?? true);
                                        console.log(`Set track ${animInfo.track} to animation: ${animInfo.name} (loop: ${animInfo.loop ?? true})`);
                                        if (animInfo.track === 0) {
                                            setCurrentAnimation(animInfo.name); // Track state for track 0 if controls shown
                                        }
                                    } catch (e) {
                                        console.error(`Error setting animation '${animInfo.name}' on track ${animInfo.track}:`, e);
                                    }
                                } else {
                                    console.warn("Skipping invalid animation info:", animInfo);
                                }
                            });
                        } else {
                            // Fallback to single initial animation logic
                            let startAnim = initialAnimation;
                            if (!anims.includes(startAnim) && anims.length > 0) {
                                startAnim = anims[0];
                            }
                            if (startAnim) {
                                console.log(`Setting single initial animation: ${startAnim}`);
                                animState.setAnimation(0, startAnim, true);
                                setCurrentAnimation(startAnim);
                            } else {
                                setCurrentAnimation(null);
                            }
                        }
                        // Set camera position to show the skeleton
                        const offset = new Vector2();
                        const size = new Vector2();
                        skeleton.getBounds(offset, size, []);

                        renderer.camera.position.x = offset.x + size.x / 2;
                        renderer.camera.position.y = offset.y + size.y / 2;

                        // Set viewport
                        const aspectRatio = canvas.width / canvas.height;
                        const viewportScale = Math.max(size.x, size.y * aspectRatio);
                        renderer.camera.viewportWidth = viewportScale * 1.2;
                        renderer.camera.viewportHeight = renderer.camera.viewportWidth / aspectRatio;

                        // Setup complete
                        lastFrameTime = Date.now() / 1000;
                        setIsLoading(false);

                        // Render function
                        const renderFrame = () => {
                            // Calculate time delta
                            const now = Date.now() / 1000;
                            const delta = now - lastFrameTime;
                            lastFrameTime = now;

                            // Clear canvas
                            context.gl.clearColor(0, 0, 0, 0);
                            context.gl.clear(context.gl.COLOR_BUFFER_BIT);

                            // Update animation
                            animState.update(delta);
                            animState.apply(skeleton);

                            // For Spine 4.2, update skeleton with delta for physics
                            if (typeof skeleton.update === 'function') {
                                skeleton.update(delta);
                            }

                            // FIX: Using empty object for physics parameter (no logging in animation loop for performance)
                            skeleton.updateWorldTransform({});

                            // Render
                            renderer.begin();
                            renderer.drawSkeleton(skeleton, true);
                            renderer.end();

                            // Continue animation loop
                            animFrameRef.current = requestAnimationFrame(renderFrame);
                        };

                        // Start render loop
                        animFrameRef.current = requestAnimationFrame(renderFrame);

                    } catch (e) {
                        console.error("Spine setup error:", e);
                        setError(`Spine setup error: ${e.message}`);
                        setIsLoading(false);
                    }
                } else {
                    // Not loaded yet - check again soon
                    setTimeout(checkLoading, 100);
                }
            };

            // Start checking for asset loading
            checkLoading();

        } catch (e) {
            console.error("Initialization error:", e);
            setError(`Initialization error: ${e.message}`);
            setIsLoading(false);
        }

        // Cleanup function
        return () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current);
                animFrameRef.current = null;
            }

            // Clean up WebGL resources
            if (spineInstanceRef.current.renderer) {
                try {
                    spineInstanceRef.current.renderer.dispose();
                } catch (e) {
                    console.warn("Error disposing renderer:", e);
                }
            }
        };
    }, [isInView, assetPath, jsonFile, atlasFile, initialAnimation, initialSkin, initialTrackAnimations]);

    // Event handler for animation selection
    const handleAnimationChange = (e) => {
        const animName = e.target.value;
        setCurrentAnimation(animName);

        if (!spineInstanceRef.current.animState) return;

        try {
            spineInstanceRef.current.animState.setAnimation(0, animName, true);
        } catch (e) {
            console.error("Animation change error:", e);
            setError(`Failed to set animation: ${e.message}`);
        }
    };

    // Event handler for skin selection
    const handleSkinChange = (e) => {
        const skinName = e.target.value;
        setCurrentSkin(skinName);

        if (!spineInstanceRef.current.skeleton) return;

        try {
            const skeleton = spineInstanceRef.current.skeleton;
            skeleton.setSkinByName(skinName);
            skeleton.setSlotsToSetupPose();

            // FIX: Based on error logs, only the empty object approach works
            console.log("Using empty object for physics parameter in skin change");
            skeleton.updateWorldTransform({});
        } catch (e) {
            console.error("Skin change error:", e);
            setError(`Failed to set skin: ${e.message}`);
        }
    };

    return (
        <PlayerContainer ref={containerRef} className={className} style={style} aria-busy={isLoading}>
            <CanvasWrapper>
                {/* Added explicit width/height for initial sizing before JS resize */}
                <canvas ref={canvasRef} width="800" height="600" />

                {/* Show poster while not in view or while loading */}
                {poster && (!isInView || isLoading) && (
                    <PosterImage
                        src={poster}
                        alt="Loading animation..."
                        loading="eager"
                    />
                )}

                {isLoading && isInView && (
                    <LoadingErrorOverlay role="status" aria-live="polite">
                        Loading...
                    </LoadingErrorOverlay>
                )}
                {error && (
                    <LoadingErrorOverlay role="alert" aria-live="assertive">
                        Error: {error}
                    </LoadingErrorOverlay>
                )}
            </CanvasWrapper>

            {/* === Conditionally Render Controls === */}
            {showControls && (
                <Controls>
                    <ControlGroup>
                        <ControlLabel htmlFor={`anim-select-${assetPath}`}>Animation</ControlLabel> {/* Unique ID */}
                        <SelectControl
                            id={`anim-select-${assetPath}`}
                            value={currentAnimation || ""}
                            onChange={handleAnimationChange}
                            disabled={isLoading || !!error || availableAnimations.length === 0}
                        >
                            {/* Default/empty option if no animation selected or available */}
                            {(availableAnimations.length === 0 || !currentAnimation) && <option value="">Select Animation</option>}
                            {availableAnimations.map(anim => (
                                <option key={anim} value={anim}>{anim}</option>
                            ))}
                        </SelectControl>
                    </ControlGroup>

                    {/* Only show skin dropdown if there are multiple skins */}
                    {availableSkins.length > 1 && (
                        <ControlGroup>
                            <ControlLabel htmlFor={`skin-select-${assetPath}`}>Skin</ControlLabel> {/* Unique ID */}
                            <SelectControl
                                id={`skin-select-${assetPath}`}
                                value={currentSkin || "default"}
                                onChange={handleSkinChange}
                                disabled={isLoading || !!error}
                            >
                                {availableSkins.map(skin => (
                                    <option key={skin} value={skin}>{skin}</option>
                                ))}
                            </SelectControl>
                        </ControlGroup>
                    )}
                </Controls>
            )}
        </PlayerContainer>
    );
}

export default InteractiveSpinePlayer;